/**
 * ProjectStore — Zustand store with Mutative middleware for undo/redo.
 *
 * All edits (user + agent) flow through pure mutation functions.
 * Mutative captures patches/inversePatches automatically.
 * The sync layer translates patches → Convex mutations.
 */

import { create } from 'zustand'
import { create as mutativeCreate, type Patch } from 'mutative'
import type { ProjectState, ClipState, EditEntry, WorkflowState } from './projectState'
import * as M from './projectMutations'

// ── Types ──────────────────────────────────────────────────

export type EditSource = 'user' | 'agent'

interface HistoryEntry {
  id: string
  label: string
  source: EditSource
  patches: Patch[]
  inversePatches: Patch[]
  timestamp: number
}

type SyncFn = (patches: Patch[], inversePatches: Patch[], label: string, source: EditSource) => void

interface ProjectStoreState extends ProjectState {
  // ── Hydration ─────────────────────────────────────────
  _hydrated: boolean
  _syncFn: SyncFn | null

  hydrate: (state: ProjectState) => void
  setSyncFn: (fn: SyncFn) => void

  // ── History ───────────────────────────────────────────
  _undoStack: HistoryEntry[]
  _redoStack: HistoryEntry[]

  canUndo: () => boolean
  canRedo: () => boolean
  undo: () => void
  redo: () => void
  getHistory: () => EditEntry[]
  loadHistory: (entries: Array<{ id: string; label: string; source: 'user' | 'agent'; patches: Patch[]; inversePatches: Patch[]; timestamp: number }>) => void
  bridgeServerEntries: (entries: Array<{ id: string; label: string; source: 'user' | 'agent'; undoOps: string; undone: boolean; invalidatedAt?: number; createdAt: number }>) => void

  // ── Remote sync ───────────────────────────────────────
  // Pending map: tracks local edits awaiting server confirmation.
  // While a field is pending, incoming server values for that field are ignored.
  _pending: Record<string, { value: unknown; expiresAt: number }>

  // Apply server state without generating patches (subscription updates).
  // Respects pending entries — local wins during optimistic window.
  applyServerState: (workflow: WorkflowState, clips: Record<string, ClipState>, clipOrder: string[]) => void

  // ── Edit API ──────────────────────────────────────────
  // Each method wraps a pure mutation, captures patches, records history.
  edit: (label: string, source: EditSource, fn: (draft: ProjectState) => void) => Patch[]

  // Convenience methods that delegate to edit()
  trimClip: (clipId: string, trimStart: number, trimEnd: number) => void
  deleteClip: (clipId: string) => void
  restoreClip: (clipId: string) => void
  createClip: (clip: ClipState) => void
  reorderClips: (newOrder: string[]) => void
  updateClipPrompt: (clipId: string, videoPrompt: string) => void
  updateClipTitle: (clipId: string, title: string) => void
  updateClipSpeed: (clipId: string, speed: number) => void
  updateClipTransition: (clipId: string, transition: Parameters<typeof M.updateClipTransition>[2]) => void
  updateClipEffects: (clipId: string, effects: Parameters<typeof M.updateClipEffects>[2]) => void
  updateClipDialog: (clipId: string, dialog: Parameters<typeof M.updateClipDialog>[2]) => void
  updateClipSceneLabel: (clipId: string, sceneLabel: string | undefined) => void
  updateClipAnimationKeyframes: (clipId: string, keyframes: Parameters<typeof M.updateClipAnimationKeyframes>[2]) => void
  batchUpdateClipEffects: (updates: Parameters<typeof M.batchUpdateClipEffects>[1]) => void
  updateNarrative: (narrative: string) => void
  updateTitle: (title: string) => void
  updateCharacters: (characters: Parameters<typeof M.updateCharacters>[1]) => void
  updateCharacter: (characterId: string, updates: Parameters<typeof M.updateCharacter>[2]) => void
  updateGlobalContext: (globalContext: string) => void
  updateSettings: (settings: Parameters<typeof M.updateSettings>[1]) => void
  updateTimelineMode: (mode: 'ripple' | 'preserve_positions') => void
  updateExportDuration: (exportDuration: number | undefined) => void
  addAudioTrack: (track: Parameters<typeof M.addAudioTrack>[1]) => void
  removeAudioTrack: (trackId: string) => void
  updateAudioTrack: (trackId: string, updates: Parameters<typeof M.updateAudioTrack>[2]) => void
  addTextOverlay: (overlay: Parameters<typeof M.addTextOverlay>[1]) => void
  updateTextOverlay: (overlayId: string, updates: Parameters<typeof M.updateTextOverlay>[2]) => void
  removeTextOverlay: (overlayId: string) => void
  setTextOverlays: (overlays: Parameters<typeof M.setTextOverlays>[1]) => void
  splitClip: (clipId: string, splitAtSeconds: number, newClipId: string) => void
}

// ── Helpers ────────────────────────────────────────────────

export const PENDING_TTL = 15_000 // 15s optimistic window

// Fields set by the server (video pipeline, generation, timestamps).
// These should never be overwritten by undo/redo patches — the server
// is authoritative for them.
function serverManagedFields(clip: ClipState): Partial<ClipState> {
  return {
    videoStatus: clip.videoStatus,
    videoUrl: clip.videoUrl,
    storyboardImageUrl: clip.storyboardImageUrl,
    firstFrameUrl: clip.firstFrameUrl,
    lastFrameUrl: clip.lastFrameUrl,
    metadata: clip.metadata,
    transcription: clip.transcription,
    transcriptionStatus: clip.transcriptionStatus,
    createdAt: clip.createdAt,
    updatedAt: clip.updatedAt,
  }
}

let idCounter = 0
function nextId(): string {
  return `edit_${Date.now()}_${++idCounter}`
}

function projectSlice(s: ProjectStoreState): ProjectState {
  return { workflow: s.workflow, clips: s.clips, clipOrder: s.clipOrder }
}

function buildPending(
  patches: Patch[],
  resultState: ProjectState,
  existing: Record<string, { value: unknown; expiresAt: number }>,
): Record<string, { value: unknown; expiresAt: number }> {
  const pending = { ...existing }
  const expiresAt = Date.now() + PENDING_TTL
  for (const patch of patches) {
    const path = patch.path as (string | number)[]
    if (path.length < 2) continue
    const keyParts = path.slice(0, Math.min(path.length, 3))
    const key = keyParts.join('.')
    let value: unknown = patch.value
    if (keyParts.length < path.length) {
      value = keyParts.reduce((obj: any, k) => obj?.[k], resultState)
    }
    pending[key] = { value, expiresAt }
  }
  return pending
}

// ── Store ──────────────────────────────────────────────────

export const useProjectStore = create<ProjectStoreState>()((set, get) => {
  // Core edit function: runs mutation on draft via Mutative, captures patches
  function edit(label: string, source: EditSource, fn: (draft: ProjectState) => void): Patch[] {
    const state = get()
    const current = projectSlice(state)

    const [nextState, patches, inversePatches] = mutativeCreate(
      current,
      (draft) => fn(draft),
      { enablePatches: { pathAsArray: true } },
    )

    if (patches.length === 0) return []

    const entry: HistoryEntry = {
      id: nextId(),
      label,
      source,
      patches,
      inversePatches,
      timestamp: Date.now(),
    }

    const pending = buildPending(patches, nextState, state._pending)

    set({
      ...nextState,
      _undoStack: [...state._undoStack, entry],
      _redoStack: [], // Clear redo on new edit
      _pending: pending,
    })

    // Dispatch sync
    if (state._syncFn) {
      state._syncFn(patches, inversePatches, label, source)
    }

    return patches
  }

  function applyPatchesLocal(base: ProjectState, patchList: Patch[]): ProjectState {
    return mutativeCreate(base, (draft: any) => {
      for (const patch of patchList) {
        const path = patch.path as (string | number)[]
        let target: any = draft
        for (let i = 0; i < path.length - 1; i++) {
          if (target == null) continue // skip this patch, not the entire callback
          target = target[path[i]]
        }
        if (target == null) continue
        const key = path[path.length - 1]
        if (patch.op === 'remove') {
          if (Array.isArray(target)) {
            target.splice(key as number, 1)
          } else {
            delete target[key]
          }
        } else {
          target[key] = patch.value
        }
      }
    }) as ProjectState
  }

  // Convenience: wrap a pure mutation in edit()
  function makeEdit(
    label: string | ((...args: any[]) => string),
    fn: (state: ProjectState, ...args: any[]) => void,
  ) {
    return (...args: any[]) => {
      const l = typeof label === 'function' ? label(...args) : label
      edit(l, 'user', (draft) => fn(draft, ...args))
    }
  }

  const emptyWorkflow: WorkflowState = {
    _id: '',
    title: '',
    narrative: '',
    characters: [],
    settings: {},
    audioTracks: [],
    textOverlays: [],
  }

  return {
    // ── State ───────────────────────────────────────────
    workflow: emptyWorkflow,
    clips: {},
    clipOrder: [],
    _hydrated: false,
    _syncFn: null,
    _undoStack: [],
    _redoStack: [],
    _pending: {},

    // ── Lifecycle ───────────────────────────────────────
    hydrate: (state: ProjectState) =>
      set({
        workflow: state.workflow,
        clips: state.clips,
        clipOrder: state.clipOrder,
        _hydrated: true,
        _undoStack: [],
        _redoStack: [],
        _pending: {},
      }),

    setSyncFn: (fn: SyncFn) => set({ _syncFn: fn }),

    // ── History ─────────────────────────────────────────
    canUndo: () => get()._undoStack.length > 0,
    canRedo: () => get()._redoStack.length > 0,

    undo: () => {
      const { _undoStack, _redoStack, _pending } = get()
      const entry = _undoStack[_undoStack.length - 1]
      if (!entry) return

      const current = projectSlice(get())
      const reverted = applyPatchesLocal(current, entry.inversePatches)

      const pending = buildPending(entry.inversePatches, reverted, _pending)

      set({
        ...reverted,
        _undoStack: _undoStack.slice(0, -1),
        _redoStack: [..._redoStack, entry],
        _pending: pending,
      })

      const { _syncFn } = get()
      if (_syncFn) {
        _syncFn(entry.inversePatches, entry.patches, `Undo: ${entry.label}`, entry.source)
      }
    },

    redo: () => {
      const { _redoStack, _undoStack, _pending } = get()
      const entry = _redoStack[_redoStack.length - 1]
      if (!entry) return

      const current = projectSlice(get())
      const reapplied = applyPatchesLocal(current, entry.patches)

      const pending = buildPending(entry.patches, reapplied, _pending)

      set({
        ...reapplied,
        _undoStack: [..._undoStack, entry],
        _redoStack: _redoStack.slice(0, -1),
        _pending: pending,
      })

      const { _syncFn } = get()
      if (_syncFn) {
        _syncFn(entry.patches, entry.inversePatches, `Redo: ${entry.label}`, entry.source)
      }
    },

    getHistory: () => {
      const { _undoStack } = get()
      return _undoStack.map((e) => ({
        id: e.id,
        label: e.label,
        source: e.source,
        timestamp: e.timestamp,
      }))
    },

    // ── Load persisted history from Convex ────────────────
    loadHistory: (entries) => {
      const historyEntries: HistoryEntry[] = entries.map((e) => ({
        id: e.id,
        label: e.label,
        source: e.source as EditSource,
        patches: e.patches,
        inversePatches: e.inversePatches,
        timestamp: e.timestamp,
      }))
      set({ _undoStack: historyEntries, _redoStack: [] })
    },

    // Bridge server history entries (agent + user) into the local undo stack.
    // Converts undoOps → Mutative patches so undo/redo stays local-first.
    bridgeServerEntries: (
      entries: Array<{ id: string; label: string; source: 'user' | 'agent'; undoOps: string; undone: boolean; invalidatedAt?: number; createdAt: number }>,
    ) => {
      const state = get()
      const existingIds = new Set([...state._undoStack, ...state._redoStack].map((e) => e.id))
      const current = projectSlice(state)

      let undoStack = [...state._undoStack]
      let redoStack = [...state._redoStack]

      for (const entry of entries) {
        if (entry.invalidatedAt) continue
        if (existingIds.has(entry.id)) continue

        const ops = JSON.parse(entry.undoOps) as Array<{
          op: string; fields?: Record<string, unknown>; clipId?: string
        }>
        // `applyOps` = the patches that result from applying undoOps; `inverseOps`
        // = the opposite. For a NOT-undone row, undoOps is the undo direction; for
        // an undone row, the server swapped undoOps to the redo direction. Assign
        // accordingly so undo/redo stay exact across a reload in both states.
        const applyOps: Patch[] = []
        const inverseOps: Patch[] = []

        for (const op of ops) {
          if (op.op === 'patch_workflow' && op.fields) {
            for (const [key, opVal] of Object.entries(op.fields)) {
              applyOps.push({ op: 'replace', path: ['workflow', key], value: opVal })
              inverseOps.push({ op: 'replace', path: ['workflow', key], value: (current.workflow as any)[key] })
            }
          } else if (op.op === 'patch_clip' && op.clipId && op.fields) {
            for (const [key, opVal] of Object.entries(op.fields)) {
              applyOps.push({ op: 'replace', path: ['clips', op.clipId!, key], value: opVal })
              inverseOps.push({ op: 'replace', path: ['clips', op.clipId!, key], value: current.clips[op.clipId!]?.[key as keyof ClipState] })
            }
          } else if (op.op === 'soft_delete_clip' && op.clipId) {
            applyOps.push({ op: 'replace', path: ['clips', op.clipId, 'deletedAt'], value: Date.now() })
            inverseOps.push({ op: 'replace', path: ['clips', op.clipId, 'deletedAt'], value: undefined })
          } else if (op.op === 'restore_clip' && op.clipId) {
            applyOps.push({ op: 'replace', path: ['clips', op.clipId, 'deletedAt'], value: undefined })
            inverseOps.push({ op: 'replace', path: ['clips', op.clipId, 'deletedAt'], value: Date.now() })
          }
        }

        if (applyOps.length === 0) continue

        const historyEntry: HistoryEntry = {
          id: entry.id,
          label: entry.label,
          source: entry.source as EditSource,
          // undoStack entries undo via inversePatches and redo via patches;
          // for an undone (redo-stack) entry, applying undoOps IS the redo.
          patches: entry.undone ? applyOps : inverseOps,
          inversePatches: entry.undone ? inverseOps : applyOps,
          timestamp: entry.createdAt,
        }

        if (entry.undone) {
          redoStack.push(historyEntry)
        } else {
          undoStack.push(historyEntry)
        }
      }

      // Sort by timestamp to maintain chronological order
      undoStack.sort((a, b) => a.timestamp - b.timestamp)
      redoStack.sort((a, b) => a.timestamp - b.timestamp)

      set({
        _undoStack: undoStack,
        _redoStack: redoStack,
      })
    },

    // ── Server sync ─────────────────────────────────────
    applyServerState: (
      workflow: WorkflowState,
      incomingClips: Record<string, ClipState>,
      _serverClipOrder: string[],
    ) => {
      const { _pending, clips: currentClips, _undoStack, _redoStack } = get()
      const now = Date.now()

      // Prune expired pending entries
      const activePending: typeof _pending = {}
      for (const [key, entry] of Object.entries(_pending)) {
        if (now < entry.expiresAt) activePending[key] = entry
      }

      // Start from server state and selectively preserve pending local edits.
      const mergedClips = { ...incomingClips }

      // Collect clip IDs referenced in undo/redo stacks
      const historyClipIds = new Set<string>()
      for (const entry of [..._undoStack, ..._redoStack]) {
        for (const patch of [...entry.patches, ...entry.inversePatches]) {
          const path = patch.path as (string | number)[]
          if (path[0] === 'clips' && typeof path[1] === 'string') {
            historyClipIds.add(path[1] as string)
          }
        }
      }

      // Preserve locally-known clips that aren't in server data
      // (soft-deleted clips the server filtered out but undo needs)
      for (const [id, clip] of Object.entries(currentClips)) {
        if (!mergedClips[id] && historyClipIds.has(id)) {
          mergedClips[id] = clip
        }
      }

      // Apply server state, respecting pending
      const mergedWorkflow = { ...workflow }

      // For each pending entry, override server value with local value.
      // Fields NOT covered by pending always come from the server, so
      // server-managed fields (videoStatus, videoUrl, etc.) stay current.
      for (const [key, entry] of Object.entries(activePending)) {
        const parts = key.split('.')
        if (parts[0] === 'workflow' && parts.length >= 2) {
          setNestedValue(mergedWorkflow, parts.slice(1), entry.value)
        } else if (parts[0] === 'clips' && parts.length === 2) {
          // Whole-clip pending (create/delete via undo/redo).
          const cid = parts[1]
          const localClip = currentClips[cid]
          if (localClip) {
            // Clip exists locally — keep local editable fields but merge
            // server-managed fields so videoUrl/videoStatus stay current.
            const serverClip = incomingClips[cid]
            if (serverClip) {
              mergedClips[cid] = { ...localClip, ...serverManagedFields(serverClip) }
            } else {
              mergedClips[cid] = localClip
            }
          } else {
            // Clip was deleted locally — keep it deleted
            delete mergedClips[cid]
          }
        } else if (parts[0] === 'clips' && parts.length >= 3) {
          const cid = parts[1]
          if (mergedClips[cid]) {
            setNestedValue(mergedClips[cid], parts.slice(2), entry.value)
          }
        }
      }

      // Derive clipOrder from merged clips instead of using server's value.
      // This prevents server sync from overwriting local undo operations
      // (e.g., un-deleting a clip that the server hasn't caught up to yet).
      const derivedOrder = Object.values(mergedClips)
        .filter((c) => !c.deletedAt)
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map((c) => c._id)

      set({
        workflow: mergedWorkflow,
        clips: mergedClips,
        clipOrder: derivedOrder,
        _hydrated: true,
        _pending: activePending,
      })
    },

    // ── Edit API ────────────────────────────────────────
    edit,

    trimClip: makeEdit(
      () => 'Trim clip',
      M.trimClip,
    ),
    deleteClip: makeEdit(
      (clipId: string) => {
        const clip = get().clips[clipId]
        return `Delete "${clip?.title || 'clip'}"`
      },
      M.deleteClip,
    ),
    restoreClip: makeEdit('Restore clip', M.restoreClip),
    createClip: makeEdit(
      (clip: ClipState) => `Create "${clip.title || 'clip'}"`,
      (state, clip) => M.createClip(state, clip),
    ),
    reorderClips: makeEdit('Reorder clips', M.reorderClips),
    updateClipPrompt: makeEdit('Update clip prompt', M.updateClipPrompt),
    updateClipTitle: makeEdit('Update clip title', M.updateClipTitle),
    updateClipSpeed: makeEdit('Change clip speed', M.updateClipSpeed),
    updateClipTransition: makeEdit('Change transition', M.updateClipTransition),
    updateClipEffects: makeEdit('Change effects', M.updateClipEffects),
    updateClipDialog: makeEdit('Update dialog', M.updateClipDialog),
    updateClipSceneLabel: makeEdit('Update scene label', M.updateClipSceneLabel),
    updateClipAnimationKeyframes: makeEdit('Update animation keyframes', M.updateClipAnimationKeyframes),
    batchUpdateClipEffects: makeEdit('Batch update effects', M.batchUpdateClipEffects),
    updateNarrative: makeEdit('Update narrative', M.updateNarrative),
    updateTitle: makeEdit('Update title', M.updateTitle),
    updateCharacters: makeEdit('Update characters', M.updateCharacters),
    updateCharacter: makeEdit('Update character', M.updateCharacter),
    updateGlobalContext: makeEdit('Update context', M.updateGlobalContext),
    updateSettings: makeEdit('Update settings', M.updateSettings),
    updateTimelineMode: makeEdit('Change timeline mode', M.updateTimelineMode),
    updateExportDuration: makeEdit('Update export duration', M.updateExportDuration),
    addAudioTrack: makeEdit(
      (track) => `Add ${track.type} track`,
      M.addAudioTrack,
    ),
    removeAudioTrack: makeEdit('Remove audio track', M.removeAudioTrack),
    updateAudioTrack: makeEdit('Update audio track', M.updateAudioTrack),
    addTextOverlay: makeEdit('Add text overlay', M.addTextOverlay),
    updateTextOverlay: makeEdit('Update text overlay', M.updateTextOverlay),
    removeTextOverlay: makeEdit('Remove text overlay', M.removeTextOverlay),
    setTextOverlays: makeEdit('Set text overlays', M.setTextOverlays),
    splitClip: makeEdit('Split clip', M.splitClip),
  }
})

// ── Debug: expose store on window in dev ──────────────────
if (import.meta.env.DEV && typeof window !== 'undefined') {
  ;(window as any).__PROJECT_STORE__ = useProjectStore
}

// ── Utility ───────────────────────────────────────────────

function setNestedValue(obj: any, path: string[], value: unknown) {
  let target = obj
  for (let i = 0; i < path.length - 1; i++) {
    if (target == null) return
    target = target[path[i]]
  }
  if (target != null) target[path[path.length - 1]] = value
}
